import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  List,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message,
  theme
} from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  CloudDownloadOutlined,
  InfoCircleTwoTone,
  WarningTwoTone
} from "@ant-design/icons";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import PageHeader from "../shared/PageHeader";
import { formatDate } from "../shared/money";
import { openBlob } from "../api";
import * as api from "./api";

// Same order as CONTENTS in utils/accounting/reports/datevPackage.js.
const CONTENTS = [
  {
    key: "buchungsstapel",
    label: "Bookings (Buchungsstapel)",
    help: "All journal entries as a DATEV EXTF file; tax is coded with BU keys."
  },
  {
    key: "stammdaten",
    label: "Customers & suppliers (Debitoren/Kreditoren)",
    help: "Master data with addresses, VAT IDs and bank details."
  },
  {
    key: "kontenbeschriftungen",
    label: "Own account names (Kontenbeschriftungen)",
    help: "Only accounts you created; the standard SKR names stay untouched."
  },
  {
    key: "belege",
    label: "Receipts with files (Belege)",
    help: "Every booked receipt as a file, named after its receipt and invoice number."
  },
  {
    key: "rechnungen",
    label: "Outgoing invoices (PDF)",
    help: "Every sent invoice as its archived PDF."
  },
  {
    key: "bank",
    label: "Bank transactions",
    help: "All statement lines with their matching and booking number."
  },
  {
    key: "kasse",
    label: "Cash book (Kassenbuch)",
    help: "Entries with running balance and day closings."
  },
  {
    key: "offene_posten",
    label: "Open items (OPOS)",
    help: "Unpaid customer invoices and supplier bills as of today."
  },
  {
    key: "anlagen",
    label: "Fixed asset register (Anlagenverzeichnis)",
    help: "Book values, additions, depreciation and disposals of the fiscal year."
  },
  {
    key: "lohn",
    label: "Payroll journal (Lohnjournal)",
    help: "Gross pay, taxes and contributions per employee and month."
  },
  {
    key: "susa",
    label: "Trial balance (SuSa)",
    help: "Opening balance, debits, credits and balance per account."
  },
  {
    key: "kostenstellen",
    label: "Cost centres (KOST1)",
    help: "Maps the cost centre codes in the bookings to your projects."
  }
];
const ALL_KEYS = CONTENTS.map(c => c.key);
const LABEL_OF = Object.fromEntries(CONTENTS.map(c => [c.key, c.label]));

const ISSUE_LABEL = {
  datev_numbers: "DATEV advisor or client number is missing",
  open_months: "Months not closed yet",
  unlocked_entries: "Entries not locked (festgeschrieben) yet",
  unbooked_belege: "Receipts in this period are not booked yet",
  unmatched_bank: "Bank transactions not matched yet",
  payroll_pending: "Payroll runs not approved yet",
  stapel_warnings: "Entries exported without tax keys",
  already_exported: "This period was already exported",
  no_entries: "No bookings in this period"
};

const SELECTION_KEY = "acct.datev.contents";
const loadSelection = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(SELECTION_KEY));
    if (Array.isArray(saved)) return saved.filter(k => ALL_KEYS.includes(k));
  } catch {
    // ignore
  }
  return ALL_KEYS;
};
const saveSelection = keys => {
  try {
    localStorage.setItem(SELECTION_KEY, JSON.stringify(keys));
  } catch {
    // ignore
  }
};

const iso = d => d.format("YYYY-MM-DD");

/** from/to for the chosen period mode. */
const rangeOf = (mode, value) => {
  if (mode === "month") return [iso(value.startOf("month")), iso(value.endOf("month"))];
  if (mode === "quarter") {
    const start = dayjs(`${value.year}-${String((value.q - 1) * 3 + 1).padStart(2, "0")}-01`);
    return [iso(start), iso(start.add(2, "month").endOf("month"))];
  }
  if (mode === "year") return [iso(value.startOf("year")), iso(value.endOf("year"))];
  return value ? [iso(value[0]), iso(value[1])] : null;
};

const bytes = n => {
  if (!n) return "–";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const DatevExport = () => {
  const { t } = useTranslation("accounting");
  const { token } = theme.useToken();
  const lastMonth = dayjs().subtract(1, "month");
  const [mode, setMode] = useState("month");
  const [month, setMonth] = useState(lastMonth);
  const [quarter, setQuarter] = useState({
    year: lastMonth.year(),
    q: Math.floor(lastMonth.month() / 3) + 1
  });
  const [year, setYear] = useState(lastMonth);
  const [custom, setCustom] = useState(null);
  const [lockedOnly, setLockedOnly] = useState(false);
  const [contents, setContents] = useState(loadSelection);
  const [check, setCheck] = useState(null);
  const [checkError, setCheckError] = useState("");
  const [checking, setChecking] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [downloading, setDownloading] = useState("");

  const range = useMemo(() => {
    const value = { month, quarter, year, custom }[mode];
    return rangeOf(mode, value);
  }, [mode, month, quarter, year, custom]);

  const loadCheck = useCallback(async () => {
    if (!range) return;
    setChecking(true);
    setCheckError("");
    try {
      const res = await api.checkExport({
        from: range[0],
        to: range[1],
        lockedOnly: lockedOnly ? "1" : undefined
      });
      setCheck(res.data);
    } catch (error) {
      setCheck(null);
      setCheckError(await api.errorText(error));
    } finally {
      setChecking(false);
    }
  }, [range, lockedOnly]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.listExports();
      setHistory(Array.isArray(res.data?.exports) ? res.data.exports : []);
    } catch (error) {
      message.error(await api.errorText(error));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCheck();
  }, [loadCheck]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const changeContents = keys => {
    const ordered = ALL_KEYS.filter(k => keys.includes(k));
    setContents(ordered);
    saveSelection(ordered);
  };

  const fileName = (from, to) => {
    const mandant = check?.settings?.mandantenNr;
    return `DATEV_${mandant ? `${mandant}_` : ""}${from.replace(/-/g, "")}_${to.replace(/-/g, "")}.zip`;
  };

  const runDownload = async (params, key) => {
    setDownloading(key);
    try {
      await openBlob(api.downloadBundle(params), fileName(params.from, params.to));
      message.success(t("DATEV package downloaded"));
      loadHistory();
      loadCheck();
    } catch (error) {
      message.error(await api.errorText(error, t("Could not build the DATEV export")));
    } finally {
      setDownloading("");
    }
  };

  const download = () => {
    const params = {
      from: range[0],
      to: range[1],
      include: contents.join(","),
      lockedOnly: lockedOnly ? "1" : undefined
    };
    const previous = check?.issues?.find(i => i.key === "already_exported");
    if (previous && contents.includes("buchungsstapel")) {
      Modal.confirm({
        rootClassName: "acct-module",
        title: t("This period was already exported"),
        content: (
          <>
            <Typography.Paragraph>
              {t(
                "If your tax advisor imports the bookings again, they are counted twice. Export again only if they asked for it or deleted the earlier batch."
              )}
            </Typography.Paragraph>
            {(previous.detail || []).slice(0, 3).map(p => (
              <div key={`${p.from}-${p.at}`}>
                {`${formatDate(p.from)} – ${formatDate(p.to)} · ${t("exported on")} ${dayjs(p.at).format("DD.MM.YYYY HH:mm")}`}
              </div>
            ))}
          </>
        ),
        okText: t("Export anyway"),
        cancelText: t("Cancel"),
        onOk: () => runDownload(params, "main")
      });
      return;
    }
    runDownload(params, "main");
  };

  const issueIcon = severity => {
    const style = { fontSize: 20 };
    if (severity === "error")
      return <CloseCircleTwoTone twoToneColor={token.colorError} style={style} />;
    if (severity === "warning")
      return <WarningTwoTone twoToneColor={token.colorWarning} style={style} />;
    return <InfoCircleTwoTone twoToneColor={token.colorInfo} style={style} />;
  };

  const issueDetail = issue => {
    if (!issue.detail) return null;
    if (typeof issue.detail === "string") return issue.detail;
    if (issue.key === "already_exported")
      return issue.detail
        .slice(0, 3)
        .map(
          p => `${formatDate(p.from)} – ${formatDate(p.to)} (${dayjs(p.at).format("DD.MM.YYYY")})`
        )
        .join(", ")
        .concat(issue.detail.length > 3 ? " …" : "");
    return issue.detail.map((d, i) => (
      <div key={i} className="acct-wrap">
        {String(d)}
      </div>
    ));
  };

  const settings = check?.settings || {};
  const counts = check?.counts || {};
  const hasError = Boolean(check?.issues?.some(i => i.severity === "error"));
  const warnings = (check?.issues || []).filter(i => i.severity !== "info");

  const periodPicker = (
    <Space wrap>
      {mode === "month" ? (
        <DatePicker
          picker="month"
          value={month}
          format="MM/YYYY"
          allowClear={false}
          onChange={v => v && setMonth(v)}
        />
      ) : null}
      {mode === "quarter" ? (
        <>
          <Select
            value={quarter.q}
            style={{ width: 90 }}
            options={[1, 2, 3, 4].map(q => ({ value: q, label: `Q${q}` }))}
            onChange={q => setQuarter(prev => ({ ...prev, q }))}
          />
          <DatePicker
            picker="year"
            value={dayjs(`${quarter.year}-01-01`)}
            allowClear={false}
            onChange={v => v && setQuarter(prev => ({ ...prev, year: v.year() }))}
          />
        </>
      ) : null}
      {mode === "year" ? (
        <DatePicker picker="year" value={year} allowClear={false} onChange={v => v && setYear(v)} />
      ) : null}
      {mode === "custom" ? (
        <DatePicker.RangePicker value={custom} format="DD.MM.YYYY" onChange={setCustom} />
      ) : null}
    </Space>
  );

  const historyColumns = [
    {
      title: t("Period"),
      key: "period",
      mobile: "title",
      render: r => `${formatDate(r.from)} – ${formatDate(r.to)}`
    },
    {
      title: t("Exported on"),
      dataIndex: "createdAt",
      mobile: "subtitle",
      render: v => dayjs(v).format("DD.MM.YYYY HH:mm")
    },
    {
      title: t("Contents"),
      dataIndex: "contents",
      mobile: "full",
      render: list =>
        (list || []).length === ALL_KEYS.length ? (
          <Tag color="blue">{t("Complete package")}</Tag>
        ) : (
          <Space size={[4, 4]} wrap>
            {(list || []).map(k => (
              <Tag key={k}>{t(LABEL_OF[k] || k)}</Tag>
            ))}
          </Space>
        )
    },
    {
      title: t("Bookings"),
      key: "rows",
      align: "right",
      render: r => r.counts?.buchungsstapel ?? "–"
    },
    { title: t("Size"), dataIndex: "bytes", align: "right", render: bytes },
    {
      title: t("Notes"),
      dataIndex: "warnings",
      render: n => (n ? <Tag color="gold">{n}</Tag> : <Tag color="green">0</Tag>)
    },
    {
      title: t("Actions"),
      key: "actions",
      render: r => (
        <Button
          size="small"
          icon={<CloudDownloadOutlined />}
          loading={downloading === String(r._id)}
          onClick={() =>
            runDownload(
              {
                from: r.from,
                to: r.to,
                include: (r.contents || []).join(","),
                lockedOnly: r.lockedOnly ? "1" : undefined
              },
              String(r._id)
            )
          }
        >
          {t("Download again")}
        </Button>
      )
    }
  ];

  return (
    <div>
      <PageHeader
        title={t("DATEV export")}
        subtitle={t(
          "Everything your tax advisor needs, prepared for DATEV Rechnungswesen, in one ZIP file."
        )}
      />
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title={t("Period")} size="small">
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Segmented
                value={mode}
                onChange={setMode}
                options={[
                  { value: "month", label: t("Month") },
                  { value: "quarter", label: t("Quarter") },
                  { value: "year", label: t("Year") },
                  { value: "custom", label: t("Custom") }
                ]}
              />
              {periodPicker}
              <Space>
                <Switch checked={lockedOnly} onChange={setLockedOnly} />
                <span>{t("Only locked (festgeschriebene) bookings")}</span>
              </Space>
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card
            title={t("Tax advisor")}
            size="small"
            extra={<Link to="/user/accounting/settings">{t("Edit")}</Link>}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t("Company")}>
                {settings.companyName || "–"}
              </Descriptions.Item>
              <Descriptions.Item label={t("Tax advisor (Steuerberater)")}>
                {settings.steuerberater || "–"}
                {settings.steuerberaterEmail ? ` · ${settings.steuerberaterEmail}` : ""}
              </Descriptions.Item>
              <Descriptions.Item label={t("DATEV advisor number (Berater-Nr.)")}>
                {settings.beraterNr || <Tag color="red">{t("missing")}</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label={t("DATEV client number (Mandanten-Nr.)")}>
                {settings.mandantenNr || <Tag color="red">{t("missing")}</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label={t("Chart of accounts")}>
                {String(settings.kontenrahmen || "").toUpperCase() || "–"}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24}>
          <Card
            title={t("Check before export")}
            size="small"
            extra={
              <Button size="small" onClick={loadCheck} loading={checking}>
                {t("Refresh")}
              </Button>
            }
          >
            {checkError ? <Alert type="error" showIcon message={checkError} /> : null}
            {check && !warnings.length ? (
              <Alert
                type="success"
                showIcon
                icon={<CheckCircleTwoTone twoToneColor={token.colorSuccess} />}
                message={t("Everything is booked and closed — ready for your tax advisor.")}
              />
            ) : null}
            {check && check.issues.length ? (
              <List
                loading={checking}
                dataSource={check.issues}
                renderItem={issue => (
                  <List.Item
                    actions={
                      issue.link
                        ? [
                            <Link key="fix" to={issue.link}>
                              {issue.severity === "error" ? t("Fix") : t("Go to page")}
                            </Link>
                          ]
                        : []
                    }
                  >
                    <List.Item.Meta
                      avatar={issueIcon(issue.severity)}
                      title={
                        <Space wrap className="acct-wrap">
                          <span>{t(ISSUE_LABEL[issue.key] || issue.key)}</span>
                          {issue.count ? (
                            <Tag color={issue.severity === "error" ? "red" : "gold"}>
                              {issue.count}
                            </Tag>
                          ) : null}
                        </Space>
                      }
                      description={issueDetail(issue)}
                    />
                  </List.Item>
                )}
              />
            ) : null}
            {check && warnings.length && !hasError ? (
              <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                {t(
                  "You can export anyway; open items are listed in Protokoll.txt inside the ZIP so your tax advisor sees them."
                )}
              </Typography.Paragraph>
            ) : null}
          </Card>
        </Col>

        <Col xs={24}>
          <Card
            title={t("Contents")}
            size="small"
            extra={
              <Space>
                <Button size="small" type="link" onClick={() => changeContents(ALL_KEYS)}>
                  {t("All")}
                </Button>
                <Button size="small" type="link" onClick={() => changeContents(["buchungsstapel"])}>
                  {t("Bookings only")}
                </Button>
              </Space>
            }
          >
            <Checkbox.Group
              value={contents}
              onChange={changeContents}
              className="acct-datev-contents"
              style={{ width: "100%" }}
            >
              <Row gutter={[16, 12]}>
                {CONTENTS.map(c => (
                  <Col xs={24} md={12} xl={8} key={c.key}>
                    <Checkbox value={c.key}>
                      <Space size={6} wrap>
                        <strong>{t(c.label)}</strong>
                        {typeof counts[c.key] === "number" ? (
                          <Tag color={counts[c.key] ? "blue" : "default"}>{counts[c.key]}</Tag>
                        ) : null}
                      </Space>
                      <div>
                        <Typography.Text type="secondary">{t(c.help)}</Typography.Text>
                      </div>
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
            <div className="acct-actions" style={{ marginTop: 16 }}>
              <Button
                type="primary"
                size="large"
                icon={<CloudDownloadOutlined />}
                loading={downloading === "main"}
                disabled={!range || !contents.length || hasError || !check}
                onClick={download}
              >
                {t("Download DATEV package")}
              </Button>
            </div>
            {hasError ? (
              <Typography.Text type="danger">
                {t("Enter the DATEV advisor and client number in the settings first.")}
              </Typography.Text>
            ) : null}
          </Card>
        </Col>

        <Col xs={24}>
          <Card title={t("Previous exports")} size="small">
            <ResponsiveTable
              rowKey="_id"
              size="small"
              loading={historyLoading}
              columns={historyColumns}
              dataSource={history}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              locale={{ emptyText: t("No exports yet") }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DatevExport;
